import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Zap, Plus, Trash2, Loader2, Power } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MAX_BOOST_PCT = 50;

const SPORTS = [
    { v: 'any', l: 'Any sport' },
    { v: 'americanfootball_nfl', l: 'NFL' },
    { v: 'basketball_nba', l: 'NBA' },
    { v: 'baseball_mlb', l: 'MLB' },
    { v: 'icehockey_nhl', l: 'NHL' },
];

// Admin panel for SGP odds-boost promos. Percentage promos auto-apply to any
// qualifying same-game parlay. Featured curated combos (created from the bet slip)
// also appear here to toggle/remove. House caps: max 50% boost, max stake $25.
export default function BoostsAdminPanel() {
    const [boosts, setBoosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        name: '', boost_pct: 25, sport: 'any', min_legs: 2, min_combined_odds: 1.0,
        max_stake: 25, subtitle: '',
    });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/sgp-boosts`, { withCredentials: true });
            setBoosts(data.boosts || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load boosts');
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const create = async () => {
        if (!form.name.trim()) { toast.error('Give the boost a name'); return; }
        if (Number(form.boost_pct) > MAX_BOOST_PCT) { toast.error(`Max boost is ${MAX_BOOST_PCT}%`); return; }
        setSaving(true);
        try {
            await axios.post(`${API}/admin/sgp-boosts`, {
                kind: 'percent',
                name: form.name.trim(),
                boost_pct: Number(form.boost_pct),
                sport: form.sport,
                min_legs: Number(form.min_legs),
                min_combined_odds: Number(form.min_combined_odds),
                max_stake: Number(form.max_stake),
                subtitle: form.subtitle.trim() || null,
                active: true,
            }, { withCredentials: true });
            toast.success('Boost created & live');
            setForm({ ...form, name: '', subtitle: '' });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Create failed');
        } finally { setSaving(false); }
    };

    const toggle = async (b) => {
        try {
            await axios.patch(`${API}/admin/sgp-boosts/${b.id}`, { active: !b.active }, { withCredentials: true });
            load();
        } catch (e) { toast.error('Update failed'); }
    };

    const remove = async (b) => {
        if (!window.confirm(`Delete boost "${b.name}"?`)) return;
        try {
            await axios.delete(`${API}/admin/sgp-boosts/${b.id}`, { withCredentials: true });
            toast.success('Boost deleted');
            load();
        } catch (e) { toast.error('Delete failed'); }
    };

    const F = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <div className="card-dark p-5 space-y-5" data-testid="boosts-admin-panel">
            <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#39FF14]" />
                <div>
                    <h3 className="text-white font-bold">SGP Odds Boosts</h3>
                    <p className="text-[11px] text-[#A0AAB2]">Percentage promos auto-apply to qualifying same-game parlays. Max {MAX_BOOST_PCT}% · payout still capped at your SGP limit.</p>
                </div>
            </div>

            {/* Create percent boost */}
            <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4 space-y-3">
                <p className="text-[11px] uppercase tracking-widest text-[#A0AAB2] font-bold">New percentage boost</p>
                <div className="grid grid-cols-2 gap-2">
                    <div className="col-span-2">
                        <label className="text-[10px] text-[#A0AAB2]">Name</label>
                        <Input value={form.name} onChange={F('name')} placeholder="e.g. Sunday NFL 25% Boost"
                            className="h-8 bg-[#121212] border-white/10 text-white text-sm" data-testid="boost-name-input" />
                    </div>
                    <div>
                        <label className="text-[10px] text-[#A0AAB2]">Boost %</label>
                        <Input type="number" value={form.boost_pct} onChange={F('boost_pct')} min={1} max={MAX_BOOST_PCT}
                            className="h-8 bg-[#121212] border-white/10 text-white text-sm" data-testid="boost-pct-input" />
                    </div>
                    <div>
                        <label className="text-[10px] text-[#A0AAB2]">Sport</label>
                        <select value={form.sport} onChange={F('sport')}
                            className="h-8 w-full rounded-md bg-[#121212] border border-white/10 text-white text-sm px-2" data-testid="boost-sport-select">
                            {SPORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] text-[#A0AAB2]">Min legs</label>
                        <Input type="number" value={form.min_legs} onChange={F('min_legs')} min={2}
                            className="h-8 bg-[#121212] border-white/10 text-white text-sm" data-testid="boost-minlegs-input" />
                    </div>
                    <div>
                        <label className="text-[10px] text-[#A0AAB2]">Max stake ($)</label>
                        <Input type="number" value={form.max_stake} onChange={F('max_stake')} min={1}
                            className="h-8 bg-[#121212] border-white/10 text-white text-sm" data-testid="boost-maxstake-input" />
                    </div>
                    <div className="col-span-2">
                        <label className="text-[10px] text-[#A0AAB2]">Storefront line (optional)</label>
                        <Input value={form.subtitle} onChange={F('subtitle')} placeholder="25% profit boost on any NFL same-game parlay!"
                            className="h-8 bg-[#121212] border-white/10 text-white text-sm" data-testid="boost-subtitle-input" />
                    </div>
                </div>
                <Button onClick={create} disabled={saving}
                    className="bg-[#39FF14] text-black hover:bg-[#2ee00d] font-bold" data-testid="boost-create-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Create boost</>}
                </Button>
            </div>

            {/* List */}
            <div className="space-y-2">
                <p className="text-[11px] uppercase tracking-widest text-[#A0AAB2] font-bold">Active & scheduled boosts</p>
                {loading ? (
                    <div className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin inline text-[#A0AAB2]" /></div>
                ) : boosts.length === 0 ? (
                    <p className="text-[11px] text-[#A0AAB2] italic py-3 text-center">No boosts yet — create one above.</p>
                ) : boosts.map((b) => (
                    <div key={b.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-[#111] p-3" data-testid={`boost-row-${b.id}`}>
                        <div className="min-w-0">
                            <p className="text-white text-sm font-bold truncate">
                                {b.name}
                                <span className="ml-2 text-[9px] uppercase px-1.5 py-0.5 rounded-full bg-white/10 text-[#A0AAB2]">{b.kind}</span>
                            </p>
                            <p className="text-[11px] text-[#A0AAB2]">
                                {b.kind === 'featured'
                                    ? `Fixed price ${b.boosted_decimal} · ${(b.legs || []).length} legs`
                                    : `+${b.boost_pct}% · ${b.sport === 'any' ? 'any sport' : b.sport} · ${b.min_legs}+ legs · max $${b.max_stake}`}
                            </p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                            <div className="flex items-center gap-1.5">
                                <Power className={`w-3.5 h-3.5 ${b.active ? 'text-[#39FF14]' : 'text-[#666]'}`} />
                                <Switch checked={!!b.active} onCheckedChange={() => toggle(b)} data-testid={`boost-toggle-${b.id}`} />
                            </div>
                            <button onClick={() => remove(b)} className="text-[#FF6B6B] hover:text-[#FF3B30]" data-testid={`boost-delete-${b.id}`}>
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
